import type { CategoriesResponse, Category } from '@/types/category';
import { SearchField } from '@/components/SearchField';
import { BRAND_LOGO_PATH, BRAND_NAME } from '@/config/deutscheBankDemo';

interface PifSidebarProps {
  categories: CategoriesResponse | null;
  categoryCompanyMatches?: Set<number>;
  selectedCategoryIds: Set<number>;
  onToggleCategory: (categoryId: number) => void;
  onResetFilters: () => void;
  loading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
}

const GROUP_LABELS: Record<string, string> = {
  sector: 'Sectors',
  investment_type: 'Investment Type',
  pool: 'Pools',
};

export function PifSidebar({
  categories,
  categoryCompanyMatches,
  selectedCategoryIds,
  onToggleCategory,
  onResetFilters,
  loading,
  search,
  onSearchChange,
}: PifSidebarProps) {
  const normalizedSearch = search.trim().toLowerCase().replace(/\s+/g, ' ');

  const filterCategories = (cats: Category[]) => {
    const withSheets = cats.filter((category) => category.sheet_count > 0);
    if (!normalizedSearch) return withSheets;

    return withSheets.filter((category) => {
      if (selectedCategoryIds.has(category.id)) {
        return true;
      }

      const categoryName = category.name.toLowerCase().replace(/\s+/g, ' ');
      return categoryName.includes(normalizedSearch) || categoryCompanyMatches?.has(category.id) === true;
    });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white md:rounded-lg md:border md:border-peach-1">
      {/* Header with Deutsche Bank logo */}
      <div className="h-16 md:h-[92px] flex items-center px-4 border-b border-peach-1 shrink-0">
        <img
          src={BRAND_LOGO_PATH}
          alt={BRAND_NAME}
          className="w-[108px] h-12 md:w-[134px] md:h-[60px] object-contain"
        />
      </div>

      {/* Search + Reset */}
      <div className="px-4 pt-6 pb-0">
        <SearchField
          value={search}
          onChange={onSearchChange}
          placeholder="Looking for a banking model"
        />
      </div>
      <div className="px-4 pt-2 pb-0">
        <button
          onClick={onResetFilters}
          className="w-full h-11 bg-peach rounded-md text-md text-grey-black hover:bg-peach-1 transition-colors"
        >
          Reset filters
        </button>
      </div>

      <div className="h-4 shrink-0" />

      {/* Category groups */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="px-4 py-2 text-sm text-grey-hover">Loading...</div>
        )}

        {categories?.groups.map((group) => {
          const hasSheets = group.categories.some((c) => c.sheet_count > 0);
          if (!hasSheets) return null;

          const filtered = filterCategories(group.categories);
          if (filtered.length === 0) return null;

          return (
            <div key={group.group} className="mb-4">
              <div className="h-11 flex items-center px-4 relative">
                <span className="text-md font-bold text-grey-black tracking-[0.3px]">
                  {GROUP_LABELS[group.group] || group.group}
                </span>
                <div className="absolute bottom-0 left-4 right-4 h-px bg-peach-1" />
              </div>
              <div className="pt-1">
                {filtered.map((cat) => (
                  <CategoryRow
                    key={cat.id}
                    category={cat}
                    selected={selectedCategoryIds.has(cat.id)}
                    onToggle={() => onToggleCategory(cat.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {!loading && categories && normalizedSearch && !categories.groups.some((group) => {
          const hasSheets = group.categories.some((category) => category.sheet_count > 0);
          if (!hasSheets) return false;
          return filterCategories(group.categories).length > 0;
        }) && (
          <div className="px-4 py-3 text-sm text-grey-hover">
            No matching filters found.
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({
  category,
  selected,
  onToggle,
}: {
  category: Category;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full h-11 flex items-center gap-2 px-4 hover:bg-peach/50 transition-colors"
    >
      <div
        className={`w-5 h-5 rounded-xs shrink-0 border transition-colors ${
          selected
            ? 'bg-pif-green border-pif-green'
            : 'bg-peach-1 border-peach-1'
        }`}
      >
        {selected && (
          <svg viewBox="0 0 16 16" className="w-5 h-5 text-white">
            <path
              d="M6.5 11.5L3 8l1-1 2.5 2.5L12 4l1 1-6.5 6.5z"
              fill="currentColor"
            />
          </svg>
        )}
      </div>
      <span className="flex-1 text-left text-md text-grey-black tracking-[0.3px] truncate">
        {category.name}
      </span>
      <span className="text-md font-medium text-grey-hover tracking-[0.3px] bg-peach px-1 rounded-[8px]">
        {category.sheet_count}
      </span>
    </button>
  );
}
